import { useState, useCallback, useRef } from "react";

const NUTRITION_API_ENABLED = true;

export interface NutritionSearchResult {
  name: string;
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  portion: string;
  brand?: string;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// FOOD TRANSLATION MAP — PT-BR / NL → EN for Open Food Facts search
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const FOOD_TRANSLATION_MAP: Record<string, string> = {
  // ━━━ PROTEÍNAS / PROTEINS / EIWITTEN ━━━
  "frango": "chicken",
  "frango grelhado": "grilled chicken",
  "frango assado": "roasted chicken",
  "peito de frango": "chicken breast",
  "coxa de frango": "chicken thigh",
  "sobrecoxa": "chicken thigh",
  "frango desfiado": "shredded chicken",
  "carne": "beef",
  "carne moida": "ground beef",
  "carne moída": "ground beef",
  "picanha": "beef sirloin",
  "alcatra": "beef rump",
  "filé mignon": "beef tenderloin",
  "file mignon": "beef tenderloin",
  "contrafilé": "beef striploin",
  "contrafile": "beef striploin",
  "costela": "beef ribs",
  "patinho": "beef round",
  "acém": "beef chuck",
  "acem": "beef chuck",
  "bisteca": "pork chop",
  "porco": "pork",
  "lombo": "pork loin",
  "bacon": "bacon",
  "presunto": "ham",
  "linguiça": "sausage",
  "linguica": "sausage",
  "salsicha": "hot dog sausage",
  "mortadela": "mortadella",
  "peru": "turkey",
  "peixe": "fish",
  "salmão": "salmon",
  "salmao": "salmon",
  "atum": "tuna",
  "tilápia": "tilapia",
  "tilapia": "tilapia",
  "sardinha": "sardine",
  "bacalhau": "cod fish",
  "camarão": "shrimp",
  "camarao": "shrimp",
  "ovo": "egg",
  "ovos": "eggs",
  "ovo mexido": "scrambled egg",
  "ovo cozido": "boiled egg",
  "ovo frito": "fried egg",
  "clara de ovo": "egg white",
  "whey": "whey protein",
  "whey protein": "whey protein",
  "proteína": "protein powder",
  "proteina": "protein powder",

  // ━━━ LATICÍNIOS / DAIRY / ZUIVELPRODUCTEN ━━━
  "leite": "milk",
  "leite integral": "whole milk",
  "leite desnatado": "skim milk",
  "leite semidesnatado": "semi skimmed milk",
  "iogurte": "yogurt",
  "iogurte grego": "greek yogurt",
  "queijo": "cheese",
  "queijo minas": "fresh cheese",
  "queijo mussarela": "mozzarella",
  "mussarela": "mozzarella",
  "queijo prato": "yellow cheese",
  "requeijão": "cream cheese",
  "requeijao": "cream cheese",
  "manteiga": "butter",
  "margarina": "margarine",
  "creme de leite": "heavy cream",
  "nata": "sour cream",

  // ━━━ CARBOIDRATOS / CARBS / KOOLHYDRATEN ━━━
  "arroz": "white rice",
  "arroz integral": "brown rice",
  "arroz branco": "white rice",
  "feijão": "black beans",
  "feijao": "black beans",
  "feijão preto": "black beans",
  "feijão carioca": "pinto beans",
  "lentilha": "lentils",
  "grão de bico": "chickpeas",
  "grao de bico": "chickpeas",
  "pão": "bread",
  "pao": "bread",
  "pão integral": "whole wheat bread",
  "pão de forma": "sandwich bread",
  "pão francês": "french bread",
  "pao frances": "french bread",
  "tapioca": "tapioca",
  "macarrão": "pasta",
  "macarrao": "pasta",
  "espaguete": "spaghetti",
  "macarrão integral": "whole wheat pasta",
  "batata": "potato",
  "batata doce": "sweet potato",
  "batata frita": "french fries",
  "mandioca": "cassava",
  "macaxeira": "cassava",
  "aipim": "cassava",
  "mandioquinha": "parsnip",
  "cuscuz": "couscous",
  "polenta": "polenta",
  "fubá": "cornmeal",
  "fuba": "cornmeal",
  "farinha": "flour",
  "farinha de trigo": "wheat flour",
  "aveia": "oats",
  "granola": "granola",
  "cereal": "cereal",
  "quinoa": "quinoa",

  // ━━━ FRUTAS / FRUITS / FRUIT ━━━
  "banana": "banana",
  "maçã": "apple",
  "maca": "apple",
  "laranja": "orange",
  "uva": "grape",
  "morango": "strawberry",
  "abacaxi": "pineapple",
  "manga": "mango",
  "mamão": "papaya",
  "mamao": "papaya",
  "melancia": "watermelon",
  "melão": "melon",
  "melao": "melon",
  "pera": "pear",
  "pêssego": "peach",
  "pessego": "peach",
  "kiwi": "kiwi",
  "limão": "lemon",
  "limao": "lemon",
  "abacate": "avocado",
  "coco": "coconut",
  "goiaba": "guava",
  "acerola": "acerola",
  "maracujá": "passion fruit",
  "maracuja": "passion fruit",
  "caju": "cashew fruit",
  "fruta": "fruit",

  // ━━━ VERDURAS E LEGUMES / VEGETABLES ━━━
  "salada": "salad",
  "alface": "lettuce",
  "tomate": "tomato",
  "cenoura": "carrot",
  "brócolis": "broccoli",
  "brocolis": "broccoli",
  "couve": "kale",
  "espinafre": "spinach",
  "abobrinha": "zucchini",
  "berinjela": "eggplant",
  "pepino": "cucumber",
  "cebola": "onion",
  "alho": "garlic",
  "pimentão": "bell pepper",
  "pimentao": "bell pepper",
  "vagem": "green beans",
  "milho": "corn",
  "ervilha": "peas",
  "beterraba": "beet",
  "couve-flor": "cauliflower",
  "couve flor": "cauliflower",
  "repolho": "cabbage",
  "agrião": "watercress",
  "agiao": "watercress",
  "rúcula": "arugula",
  "rucula": "arugula",

  // ━━━ OLEAGINOSAS / NUTS / NOTEN ━━━
  "castanha": "brazil nut",
  "castanha do pará": "brazil nut",
  "amendoim": "peanut",
  "pasta de amendoim": "peanut butter",
  "amêndoa": "almond",
  "amendoa": "almond",
  "nozes": "walnuts",
  "castanha de caju": "cashew nut",
  "pistache": "pistachio",
  "avelã": "hazelnut",
  "avela": "hazelnut",
  "semente de chia": "chia seeds",
  "chia": "chia seeds",
  "linhaça": "flaxseed",
  "linhaca": "flaxseed",
  "semente de girassol": "sunflower seeds",

  // ━━━ REFEIÇÕES BRASILEIRAS / BRAZILIAN MEALS ━━━
  "feijoada": "feijoada black bean stew",
  "coxinha": "chicken croquette",
  "pão de queijo": "cheese bread",
  "pao de queijo": "cheese bread",
  "brigadeiro": "chocolate truffle",
  "misto quente": "grilled cheese sandwich",
  "açaí": "acai bowl",
  "acai": "acai bowl",
  "vitamina": "fruit smoothie",
  "suco": "fruit juice",
  "café com leite": "coffee with milk",
  "cafe com leite": "coffee with milk",

  // ━━━ HOLANDÊS / DUTCH ━━━
  "kip": "chicken",
  "rundvlees": "beef",
  "varkensvlees": "pork",
  "vis": "fish",
  "ei": "egg",
  "eieren": "eggs",
  "melk": "milk",
  "kaas": "cheese",
  "yoghurt": "yogurt",
  "brood": "bread",
  "rijst": "rice",
  "pasta": "pasta",
  "aardappel": "potato",
  "zoete aardappel": "sweet potato",
  "havermout": "oats",
  "banaan": "banana",
  "appel": "apple",
  "sinaasappel": "orange",
  "spinazie": "spinach",
  "broccoli": "broccoli",
  "wortel": "carrot",
  "tomaat": "tomato",
  "zalm": "salmon",
  "tonijn": "tuna",
  "garnalen": "shrimp",
  "boter": "butter",
  "olie": "oil",
  "noten": "nuts",
  "amandelen": "almonds",
  "pindakaas": "peanut butter",
  "kwark": "quark",
  "hüttenkäse": "cottage cheese",
};

// ── Translation lookup ─────────────────────────────────────────────────

function translateQuery(query: string): string | null {
  const q = query.toLowerCase().trim();

  // Exact match
  if (FOOD_TRANSLATION_MAP[q]) return FOOD_TRANSLATION_MAP[q];

  // Longest partial match: find the longest key contained in the query
  let best: string | null = null;
  let bestLen = 0;
  for (const key of Object.keys(FOOD_TRANSLATION_MAP)) {
    if (q.includes(key) && key.length > bestLen) {
      best = FOOD_TRANSLATION_MAP[key];
      bestLen = key.length;
    }
  }
  if (best) return best;

  // Reverse partial: find any key that contains the query
  for (const key of Object.keys(FOOD_TRANSLATION_MAP)) {
    if (key.includes(q)) return FOOD_TRANSLATION_MAP[key];
  }

  return null;
}

// ── Parse API response ─────────────────────────────────────────────────

function parseProducts(products: any[], fallbackName: string): NutritionSearchResult[] {
  return products
    .map((p) => ({
      name: p.product_name || fallbackName,
      kcal: Math.round(p.nutriments?.["energy-kcal_100g"] || p.nutriments?.["energy-kcal"] || 0),
      protein: Math.round((p.nutriments?.proteins_100g || 0) * 10) / 10,
      carbs: Math.round((p.nutriments?.carbohydrates_100g || 0) * 10) / 10,
      fat: Math.round((p.nutriments?.fat_100g || 0) * 10) / 10,
      portion: p.serving_size || "100g",
      brand: p.brands || undefined,
    }))
    .filter((r) => r.kcal > 0 || r.protein > 0);
}

function buildUrl(terms: string, lang: "pt" | "en" = "pt"): string {
  const lc = lang === "pt" ? "pt" : "en";
  const cc = lang === "pt" ? "br" : "us";
  return `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(terms)}&search_simple=1&action=process&json=1&page_size=10&lc=${lc}&cc=${cc}&fields=product_name,brands,nutriments,serving_size`;
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useNutritionSearch() {
  const [results, setResults] = useState<NutritionSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const search = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    if (!NUTRITION_API_ENABLED) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSearching(true);
    try {
      const translation = translateQuery(query);
      const fetches: Promise<NutritionSearchResult[]>[] = [
        fetch(buildUrl(query, "pt"), { signal: controller.signal })
          .then((r) => r.json())
          .then((j) => parseProducts(j.products || [], query)),
      ];

      if (translation && translation.toLowerCase() !== query.toLowerCase().trim()) {
        fetches.push(
          fetch(buildUrl(translation, "en"), { signal: controller.signal })
            .then((r) => r.json())
            .then((j) => parseProducts(j.products || [], translation)),
        );
      }

      const arrays = await Promise.all(fetches);
      const all = arrays.flat();

      // Deduplicate by exact name (case-insensitive, trimmed)
      const seen = new Set<string>();
      const deduped = all.filter((r) => {
        const key = r.name.toLowerCase().trim();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Sort by macro completeness: prefer items with more non-zero fields
      deduped.sort((a, b) => {
        const scoreA = (a.kcal > 0 ? 1 : 0) + (a.protein > 0 ? 1 : 0) + (a.carbs > 0 ? 1 : 0) + (a.fat > 0 ? 1 : 0);
        const scoreB = (b.kcal > 0 ? 1 : 0) + (b.protein > 0 ? 1 : 0) + (b.carbs > 0 ? 1 : 0) + (b.fat > 0 ? 1 : 0);
        return scoreB - scoreA;
      });

      setResults(deduped.slice(0, 5));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const clear = useCallback(() => {
    setResults([]);
    setError(null);
    abortRef.current?.abort();
  }, []);

  return { results, searching, error, search, clear };
}
